import { Directive, ElementRef, inject, input, TemplateRef } from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PopOverService } from '@entscheidungsnavi/widgets/popover';
import { ComponentType } from '@angular/cdk/portal';
import { ErrorFeedbackService } from './error-feedback.service';

/**
 * Directive to attach an error popover to a DOM element. This directive listens for error events
 * and displays a contextual error message when triggered. It interacts with the {@link ErrorFeedbackService}
 * and the {@link PopOverService} to dynamically create and position the error popover.
 *
 */
@Directive({
  selector: '[dtErrorPopoverAnchor]',
})
export class ErrorPopoverAnchorDirective {
  private errorFeedbackService = inject(ErrorFeedbackService);
  private popOverService = inject(PopOverService);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  /**
   * A key used as an identifier for {@link ErrorFeedbackService} to attach the popover to at the correct element.
   */
  dtErrorPopoverAnchorKey = input<string>();

  /**
   * Template reference to render the popover.
   * @see PopOverService
   */
  dtErrorPopoverAnchorTemplate = input<TemplateRef<any> | ComponentType<any>>();

  /**
   * Defines positioning of popover.
   * @see PopOverService
   */
  dtErrorPopoverAnchorPosition = input<ConnectedPosition[]>([
    { originX: 'center', overlayX: 'center', originY: 'top', overlayY: 'bottom' },
  ]);

  constructor() {
    this.errorFeedbackService.events$.pipe(takeUntilDestroyed()).subscribe(async e => {
      if (e.key instanceof ElementRef && this.elementRef.nativeElement !== e.key.nativeElement) return;
      if (typeof e.key === 'string' && e.key !== this.dtErrorPopoverAnchorKey()) return;
      let popOver;
      if (this.dtErrorPopoverAnchorTemplate()) {
        popOver = this.popOverService.open(this.dtErrorPopoverAnchorTemplate(), this.elementRef, {
          templateContext: { errors: e.errors },
          position: this.dtErrorPopoverAnchorPosition(),
          hasBackdrop: true,
          autoCloseDelay: 3000,
        });
      } else {
        const { ErrorCardComponent: errorCardComponent } = await import('./error-card.component');
        popOver = this.popOverService.open(errorCardComponent, this.elementRef, {
          position: this.dtErrorPopoverAnchorPosition(),
          hasBackdrop: true,
          autoCloseDelay: 3000,
        });
        popOver.componentInstance.errors = e.errors;
      }
    });
  }
}
